import React, { useContext } from 'react';
import { SummaryDisclosure } from './DisclosureUnits/SummaryDisclosure';
import { Cocktail } from './Cocktail';
import { LAST_SECTION_INDEX } from '../../../config/constants';
import { PensionContext } from '../../../contexts/pension-robot/PensionContext';
import { isPensionCalculatedTogether } from '../../../utils/functions';
import { useCalculations } from '../../../hooks/netGrossCalculations';
import { BNDListArrows } from './LegalSection';
import { toPrice } from '../../../utils/parsers';
import { useTranslation } from 'react-i18next';

export const PrintSection: React.FC<{}> = () => {
    const { t } = useTranslation('pension');
    const {
        parameters: { personalInfo },
    } = useContext(PensionContext);

    const withPartner = isPensionCalculatedTogether(personalInfo);

    return (
        <>
            <PrintPage pageNumber={1} totalPages={3}>
                <span className={'font-medium text-2xl'}>{t('pension-save-mybnd-print-summary-title')}</span>
                <SummaryDisclosure isForPrinting={true} />
            </PrintPage>
            <PrintPage pageNumber={2} totalPages={3}>
                <AdditionalInformation isPartner={false} />
                {withPartner && <AdditionalInformation isPartner={true} />}
            </PrintPage>
            <PrintPage pageNumber={3} totalPages={3}>
                <Cocktail isColorGoal={false} pageNum={LAST_SECTION_INDEX} isForPrinting={true} />
            </PrintPage>
        </>
    );
};

const PrintPage: React.FC<{ pageNumber?: number; totalPages?: number; children?: React.ReactNode }> = ({ pageNumber, totalPages, children }) => {
    //Could maybe be done in a more elegant way, but works effectively

    const styleObject: any = pageNumber !== 1 ? { pageBreakBefore: 'always' } : {};

    return (
        <div style={styleObject} className={'p-6'}>
            <span className={'float-right font-medium'}>
                {pageNumber}/{totalPages}
            </span>
            {children}
        </div>
    );
};

const AdditionalInformation: React.FC<{ isPartner: boolean }> = ({ isPartner }) => {
    const { t } = useTranslation('pension');
    const {
        parameters: { personalInfo, pensionInfo },
    } = useContext(PensionContext);

    const infoPrefix = isPartner ? 'partner' : 'self';
    const calcPrefix = isPartner ? 'partner' : 'me';

    const { supplementsCalculated } = useCalculations();

    return (
        <>
            <div className={`font-medium text-2xl ${isPartner ? 'mt-8' : ''}`}>
                {GetCorrectPrefix(isPartner) + t('pension-save-mybnd-print-additional-title')}
            </div>
            <div className={'p-4'}>
                <BNDListArrows className={'p-4'}>
                    {personalInfo[infoPrefix].age && (
                        <SummaryItem
                            isPartner={isPartner}
                            isMoney={false}
                            numberToDisplay={personalInfo[infoPrefix].age as number}
                            textToDisplay={t('pension-save-mybnd-print-additional-age')}
                        />
                    )}

                    {personalInfo[infoPrefix].salary && (
                        <SummaryItem
                            isPartner={isPartner}
                            isMoney={true}
                            numberToDisplay={(personalInfo[infoPrefix].salary as number) * 100}
                            textToDisplay={t('pension-save-mybnd-print-additional-income')}
                        />
                    )}

                    {!isPartner &&
                        (personalInfo.maritalStatus === 'single'
                            ? pensionInfo?.self?.aowGrossYearly?.single && (
                                  <SummaryItem
                                      isPartner={isPartner}
                                      isMoney={true}
                                      numberToDisplay={pensionInfo?.self?.aowGrossYearly?.single}
                                      textToDisplay={t('pension-save-mybnd-print-additional-aow')}
                                  />
                              )
                            : pensionInfo?.self?.aowGrossYearly?.together && (
                                  <SummaryItem
                                      isPartner={isPartner}
                                      isMoney={true}
                                      numberToDisplay={pensionInfo?.self?.aowGrossYearly?.together}
                                      textToDisplay={t('pension-save-mybnd-print-additional-aow')}
                                  />
                              ))}

                    {isPartner && pensionInfo?.partner?.aowGrossYearly && (
                        <SummaryItem
                            isPartner={isPartner}
                            isMoney={true}
                            numberToDisplay={pensionInfo?.partner?.aowGrossYearly}
                            textToDisplay={t('pension-save-mybnd-print-additional-aow')}
                        />
                    )}

                    {pensionInfo && pensionInfo[infoPrefix]?.employeePensionGrossYearly && (
                        <SummaryItem
                            isPartner={isPartner}
                            isMoney={true}
                            numberToDisplay={pensionInfo[infoPrefix]?.employeePensionGrossYearly as number}
                            textToDisplay={t('pension-save-mybnd-print-additional-pension')}
                        />
                    )}

                    {supplementsCalculated[calcPrefix].yearly && (
                        <SummaryItem
                            isPartner={isPartner}
                            isMoney={true}
                            numberToDisplay={supplementsCalculated[calcPrefix].yearly}
                            textToDisplay={t('pension-save-mybnd-print-additional-supplements')}
                        />
                    )}
                </BNDListArrows>
            </div>
        </>
    );
};

const GetCorrectPrefix = (isPartner: boolean) => {
    const { t } = useTranslation('pension');

    return isPartner
        ? t('pension-save-mybnd-print-additional-yourpartners')
        : t('pension-save-mybnd-print-additional-your');
};

const SummaryItem: React.FC<{
    isPartner: boolean;
    isMoney: boolean;
    textToDisplay: string;
    numberToDisplay: number;
}> = ({ isPartner, isMoney, textToDisplay, numberToDisplay }) => {
    return (
        <div>
            <span>{GetCorrectPrefix(isPartner) + textToDisplay} </span>
            <span className={'font-medium'}>
                {isMoney
                    ? toPrice(numberToDisplay, {
                          onlyFullEuros: true,
                          withCurrencySymbol: true,
                      })
                    : numberToDisplay}
            </span>
        </div>
    );
};
